/**
 * Functional Design Document routes.
 *
 *   POST /tenders/:id/fdd/generate   — generate FDD from tender attachments
 *   GET  /tenders/:id/fdd            — load saved FDD
 *   GET  /tenders/:id/fdd/export?format=pdf|docx — download
 */

import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, tenderDocumentsTable, companiesTable, tenderFddsTable,
} from "@workspace/db";
import { eq, and, sql } from "drizzle-orm";
import fs from "fs";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { generateFddFromDocuments, fddForExport } from "../lib/fdd.js";
import { generateFddDocx, generateFddPdf } from "../lib/fddExport.js";
import { extractTextFromPdfWithGemini, shouldRunOcrFallback } from "../lib/ocr.js";

const router: any = Router();

async function ensureColumns() {
  try {
    await db.execute(sql`
      CREATE TABLE IF NOT EXISTS tender_fdds (
        id SERIAL PRIMARY KEY,
        tender_id INTEGER NOT NULL UNIQUE,
        language TEXT NOT NULL DEFAULT 'ar',
        overview TEXT,
        functional_requirements TEXT,
        non_functional_requirements TEXT,
        data_requirements TEXT,
        interfaces TEXT,
        constraints TEXT,
        assumptions TEXT,
        acceptance_criteria TEXT,
        created_at TIMESTAMP NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMP NOT NULL DEFAULT NOW()
      )
    `);
  } catch { /* ignore */ }
}
ensureColumns();

async function loadTenderOrFail(req: AuthRequest, res: any) {
  const companyId = req.companyId;
  if (!companyId) { res.status(400).json({ error: "No company profile" }); return null; }
  const rows = await db.select().from(tendersTable)
    .where(and(eq(tendersTable.id, Number(req.params.id)), eq(tendersTable.companyId, companyId)))
    .limit(1);
  if (!rows[0]) { res.status(404).json({ error: "Tender not found" }); return null; }
  return rows[0];
}

/* ── Generate FDD from attachments ── */
router.post("/tenders/:id/fdd/generate", requireAuth, async (req: AuthRequest, res: any, next: any) => {
  try {
    const tender = await loadTenderOrFail(req, res);
    if (!tender) return;
    const tenderId = tender.id;
    const language = ((req.body?.language as string) || tender.language || "ar") as "ar" | "en";

    const docs = await db.select({
      id: tenderDocumentsTable.id,
      extractedText: tenderDocumentsTable.extractedText,
      filePath: tenderDocumentsTable.filePath,
      fileSize: tenderDocumentsTable.fileSize,
      fileName: tenderDocumentsTable.fileName,
    }).from(tenderDocumentsTable).where(eq(tenderDocumentsTable.tenderId, tenderId));

    if (docs.length === 0) {
      return res.status(400).json({
        error: language === "ar"
          ? "لا توجد مرفقات مرفوعة لهذه المناقصة. يُرجى رفع المستندات أولاً."
          : "No attachments uploaded for this tender. Please upload documents first.",
      });
    }

    // For docs missing extractedText, attempt extraction from disk (pdf-parse → Gemini OCR)
    const textParts: string[] = [];
    for (const doc of docs) {
      let text = (doc.extractedText ?? "").trim();

      if (!text && doc.filePath && fs.existsSync(doc.filePath)) {
        try {
          const buf = fs.readFileSync(doc.filePath);
          // Try pdf-parse first (fast, text-based PDFs)
          try {
            const pdfModule = (await import("pdf-parse")) as any;
            const pdfParse: (buf: Buffer) => Promise<{ text: string }> = pdfModule.default ?? pdfModule;
            const parsed = await pdfParse(buf);
            text = (parsed.text ?? "").trim();
          } catch { /* pdf-parse failed, fall through to OCR */ }

          // If still thin, use Gemini OCR
          if (shouldRunOcrFallback(text, doc.fileSize ?? buf.length)) {
            const ocrResult = await extractTextFromPdfWithGemini(buf, { language });
            if (ocrResult?.text && ocrResult.text.length > 50) {
              text = ocrResult.text;
              // Persist so future calls are fast
              await db.execute(sql`UPDATE tender_documents SET extracted_text = ${text}, ocr_status = 'completed' WHERE id = ${doc.id}`);
            }
          } else if (text) {
            // Persist the pdf-parse result so future calls don't re-extract
            await db.execute(sql`UPDATE tender_documents SET extracted_text = ${text} WHERE id = ${doc.id}`);
          }
        } catch { /* ignore per-file errors */ }
      }

      if (text) textParts.push(`[${doc.fileName}]\n${text}`);
    }

    const docsText = textParts.join("\n\n──────────\n\n");

    if (!docsText) {
      return res.status(400).json({
        error: language === "ar"
          ? "تعذّر استخراج نص من المرفقات. تأكّد أن الملفات مقروءة أو أعِد رفعها. إن كانت مسحاً ضوئياً تأكّد من تفعيل خاصية OCR."
          : "Could not extract text from the uploaded attachments. Ensure files are readable or re-upload them.",
      });
    }

    const fdd = await generateFddFromDocuments({
      language, tenderTitle: tender.title, documentsText: docsText,
    });

    // Upsert
    const existing = await db.select().from(tenderFddsTable)
      .where(eq(tenderFddsTable.tenderId, tenderId)).limit(1);
    if (existing[0]) {
      const updated = await db.update(tenderFddsTable)
        .set({ language, ...fdd, updatedAt: new Date() })
        .where(eq(tenderFddsTable.tenderId, tenderId))
        .returning();
      return res.json(updated[0]);
    }
    const inserted = await db.insert(tenderFddsTable)
      .values({ tenderId, language, ...fdd })
      .returning();
    res.json(inserted[0]);
  } catch (err) { next(err); }
});

/* ── Get saved FDD ── */
router.get("/tenders/:id/fdd", requireAuth, async (req: AuthRequest, res: any, next: any) => {
  try {
    const tender = await loadTenderOrFail(req, res);
    if (!tender) return;
    const rows = await db.select().from(tenderFddsTable)
      .where(eq(tenderFddsTable.tenderId, tender.id)).limit(1);
    res.json(rows[0] ?? null);
  } catch (err) { next(err); }
});

/* ── Export FDD as PDF or DOCX ── */
router.get("/tenders/:id/fdd/export", requireAuth, async (req: AuthRequest, res: any, next: any) => {
  try {
    const tender = await loadTenderOrFail(req, res);
    if (!tender) return;
    const format = (req.query.format as string) === "pdf" ? "pdf" : "docx";

    const rows = await db.select().from(tenderFddsTable)
      .where(eq(tenderFddsTable.tenderId, tender.id)).limit(1);
    if (!rows[0]) {
      return res.status(404).json({
        error: tender.language === "en"
          ? "No FDD generated yet for this tender."
          : "لم يتم إنشاء وثيقة تصميم وظيفي لهذه المناقصة بعد.",
      });
    }
    const lang = (rows[0].language === "en" ? "en" : "ar") as "ar" | "en";

    const companyRows = await db.select().from(companiesTable)
      .where(eq(companiesTable.id, req.companyId!)).limit(1);
    const companyName = (companyRows[0] as any)?.name ?? "";

    const fdd = fddForExport(rows[0]);
    const safe = tender.title.replace(/[^\w\u0600-\u06FF]/g, "_").slice(0, 40);
    const docTitle = lang === "ar" ? "FDD_وثيقة_التصميم_الوظيفي" : "FDD_Functional_Design";
    const filename = `${docTitle}_${safe}.${format}`;

    if (format === "docx") {
      const buf = await generateFddDocx(fdd, tender.title, companyName, lang);
      res.setHeader("Content-Type", "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
      res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(filename)}`);
      return res.send(buf);
    } else {
      const buf = await generateFddPdf(fdd, tender.title, companyName, lang);
      res.setHeader("Content-Type", "application/pdf");
      res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(filename)}`);
      return res.send(buf);
    }
  } catch (err) { next(err); }
});

export default router;
